import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  ButtonsWrapper,
  DeleteModal,
  ICONS,
  PLATFORM_THEME_COLORS,
  Typography,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';
import { PERMISSION_ACTIONS, PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';

import { useRouteQuery } from 'src/common/helpers';

import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetEmployeeQuestionnaireSetting } from 'src/hooks/employee-questionnaire-settings/get-employee-questionnaire-settings.hook';
import { useDeleteEmployeeQuestionnaireSettingById } from 'src/hooks/employee-questionnaire-settings/delete-employee-questionnaire-settings-by-id.hook';

import PermissionCheck from 'src/components/permission-check';
import { EmployeeQuestionnaireSettingsTable } from './components/employee-questionnaire-settings-table';
import ChangeEmployeeQuestionnaireSettings from './components/change-employee-questionnaire-settings/change-employee-questionnaire-settings.component';

import { ROUTES } from 'src/components/app/app-routes.constants';
import { SETTINGS_PAGES } from 'src/pages/settings/settings.constants';

import {
  DEFAULT_QUESTIONNAIRES_TO_DELETE,
  QUESTIONNAIRES_MODALS,
} from './employee-questionnaire-settings.constants';

import { EmployeeQuestionnaireSettingWrapper } from './employee-questionnaire-settings.styles';

const EmployeeQuestionnaireSettings = () => {
  const navigate = useNavigate();
  const query = useRouteQuery();
  const { t } = useTranslation('settings');
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [questionnaireToDelete, setQuestionnaireToDelete] = useState(
    DEFAULT_QUESTIONNAIRES_TO_DELETE
  );

  const currentCompanyId = useGetCurrentCompanyId();
  const {
    collection,
    paginationConfig,
    onTableChange,
    loading,
  } = useGetEmployeeQuestionnaireSetting(currentCompanyId);

  const id: string = query.get('id') || '';

  const handleCreateNewQuestioner = () => {
    navigate(`${ROUTES.settings}?type=${SETTINGS_PAGES.employeeQuestionnaire}&id=new`);
  };

  const closeModal = () => {
    setActiveModal(null);
    setQuestionnaireToDelete(DEFAULT_QUESTIONNAIRES_TO_DELETE);
  };

  const deleteEmployeeQuestionnaireSettingById = useDeleteEmployeeQuestionnaireSettingById({
    onSuccess: closeModal,
  });

  const deleteEmployeeQuestionnaireSetting = () => {
    deleteEmployeeQuestionnaireSettingById({
      variables: { id: questionnaireToDelete?._id },
    });
    setQuestionnaireToDelete(DEFAULT_QUESTIONNAIRES_TO_DELETE);
    closeModal();
  };

  const onSubmitDelete = () => {
    deleteEmployeeQuestionnaireSetting();
  };

  return (
    <Fragment>
      {id ? (
        <ChangeEmployeeQuestionnaireSettings />
      ) : (
        <EmployeeQuestionnaireSettingWrapper>
          <DeleteModal
            translate={t}
            isOpened={activeModal === QUESTIONNAIRES_MODALS.delete}
            title={t('employeeQuestionnaire.deleteModal.title')}
            subTitle={t('employeeQuestionnaire.deleteModal.subtitle', {
              name: questionnaireToDelete.name,
            })}
            onClose={closeModal}
            onSubmit={onSubmitDelete}
          />
          <div>
            <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
              {t('employeeQuestionnaire.labels.employeeQuestionnaire')}
            </Typography>
            <PermissionCheck
              permission={PLATFORM_PERMISSIONS.companySettings}
              action={PERMISSION_ACTIONS.create}
            >
              <ButtonsWrapper>
                <Button
                  type="primary"
                  ghost
                  icon={ICONS.plus}
                  iconColor={PLATFORM_THEME_COLORS.primary.blue}
                  onClick={handleCreateNewQuestioner}
                >
                  {t('employeeQuestionnaire.buttons.addNewQuestionnaire')}
                </Button>
              </ButtonsWrapper>
            </PermissionCheck>
          </div>
          <EmployeeQuestionnaireSettingsTable
            dataSource={collection}
            paginationConfig={paginationConfig}
            onTableChange={onTableChange}
            handleModals={(modal, record) => {
              setActiveModal(modal);
              setQuestionnaireToDelete(record);
            }}
            loading={loading}
          />
        </EmployeeQuestionnaireSettingWrapper>
      )}
    </Fragment>
  );
};

export default EmployeeQuestionnaireSettings;
